import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ImportExportComponent } from 'app/modules/shared/components/import-export/import-export.component';
import { UsersListComponent } from 'app/modules/core/components/users-list/users-list.component';
import { AuthRepository, UserRoles } from 'app/state/auth.repository';
import { TenantsRepository } from 'app/state/tenants.repository';
import {
  errorInterface,
  ImportedUser,
  userSortOptions,
  UsersRepository,
} from 'app/state/users.repository';
import { UsersService } from 'app/api/services/users.service';
import {
  ConfirmationService,
  ConfirmEventType,
  MenuItem,
  MessageService,
} from 'primeng/api';
import { BlobStorageService } from 'app/shared/azure-blob/blob-storage.service';
import { UserForListDto } from 'app/api/models/user/userForListDto';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserGroupsSelectionComponent } from 'app/modules/shared/components/data-selection/userGroup-selection-modal/usergroups-selection.component';
import { UserGroupsService } from 'app/api/services/usergroup.service';
import { TranslocoService } from '@jsverse/transloco';
import { MessageText, ToastService } from 'app/helpers/services/toastService';
import { TrackRender } from 'app/shared/front-perfomance';
import {
  RenderTrackerService,
  whenRendered,
} from 'app/shared/renderer-tracker-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BreadcrumbService } from 'app/modules/shared/components/breadcrumb/breadcrumb.service';

@UntilDestroy()
@TrackRender()
@Component({
  selector: 'app-users-list-page',
  templateUrl: './users-list-page.component.html',
  styleUrls: ['./users-list-page.component.scss'],
  providers: [DialogService, ConfirmationService, MessageService],
  standalone: false,
})
export class UsersListPageComponent implements OnInit {
  sortOptions = userSortOptions;
  innerWidth = 0;
  searchFilter: string = '';
  searchString: string = '';
  submitErrors: string[] | null = null;
  hasErrors: boolean = false;
  waitingForImportResponse$ = false;
  users: Array<UserForListDto> = [];
  importFactory = (file: File) => this.service.importUsers(file);
  exportFactory = () => this.service.export();

  items: MenuItem[] = [];
  selectedUsers: UserForListDto[] = [];
  emptySelectedUsers: UserForListDto[] = [];
  isTenantAdmin = false;
  roleFilterValue: any = null;
  groupFilterValue: any = null;
  filtersSaved = false;

  @ViewChild(ImportExportComponent) importExport?: ImportExportComponent;
  @ViewChild(UsersListComponent) usersList?: UsersListComponent;

  ref: DynamicDialogRef | null | undefined;
  constructor(
    public tenantRepo: TenantsRepository,
    public repo: UsersRepository,
    public service: UsersService,
    public auth: AuthRepository,
    public blobService: BlobStorageService,
    public dialogService: DialogService,
    public userGroupsService: UserGroupsService,
    public confirmationService: ConfirmationService,
    public translocoService: TranslocoService,
    public toastService: ToastService,
    private renderTracker: RenderTrackerService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.fillActions();
  }

  activeTenant = localStorage.getItem('activeTenantId');
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.innerWidth = window.innerWidth;
  }

  ngOnInit(): void {
    this.getAllUsers();
    this.innerWidth = window.innerWidth;
    this.fillActions();
    this.auth.isTenantAdmin$.pipe(untilDestroyed(this)).subscribe((isAdmin) => {
      this.isTenantAdmin = isAdmin;
      this.fillActions();
    });
    this.translocoService
      .selectTranslation()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.fillActions());
    this.seedFiltersFromStorage();
  }

  private seedFiltersFromStorage(): void {
    const saved = localStorage.getItem('user-filters');
    if (!saved) {
      return;
    }
    try {
      const parsed = JSON.parse(saved);
      this.roleFilterValue = parsed.selectedRole ?? null;
      this.groupFilterValue = parsed.selectedGroup ?? null;
      this.searchString = parsed.searchString ?? '';
      this.searchFilter = this.searchString;
      this.filtersSaved = true;
    } catch {
      // Ignore malformed saved filters.
    }
  }

  toggleSaveFilters() {
    this.usersList?.toggleSaveFilters();
    this.filtersSaved = this.usersList?.checked ?? false;
  }

  filterByName(str: string): void {
    this.searchString = str ?? '';
    this.searchFilter = str ?? '';
  }

  // Stable array reference: a getter returning a fresh array on every change
  // detection cycle makes ng-select re-render its options each tick, which
  // destroys the option DOM mid-click and swallows the selection.
  roleFilterOptions = [
    { label: 'User', value: 'User' },
    { label: 'PartnerManager', value: 'Manager' },
    { label: 'Admin', value: 'Admin' },
    { label: 'Superadmin', value: 'Superadmin' },
  ];

  private groupOptionsSrc: any = undefined;
  private groupOptionsCache: { label: string; value: any }[] = [];
  get groupFilterOptions() {
    const src = this.usersList?.userGroups;
    if (src !== this.groupOptionsSrc) {
      this.groupOptionsSrc = src;
      this.groupOptionsCache = [
        {
          label: this.translocoService.translate('withoutGroup'),
          value: 'no-group',
        },
        ...(src ?? []).map((g: any) => ({ label: g.label, value: g.value })),
      ];
    }
    return this.groupOptionsCache;
  }

  onRoleFilterChange(value: any) {
    console.log(value);
    this.roleFilterValue = value ?? null;
    this.usersList?.onRoleChange(this.roleFilterValue);
  }

  onGroupFilterChange(value: any) {
    this.groupFilterValue = value ?? null;
    this.usersList?.filterByGroup(this.groupFilterValue);
  }

  getAllUsers() {
    this.service.loadAllUsers().subscribe((data) => {
      this.users = [];
      this.selectedUsers = [];
      this.emptySelectedUsers = [];
      this.updateSelectionItems();
      const chunkSize = 50;
      let index = 0;

      const loadChunk = () => {
        const chunk = data.slice(index, index + chunkSize);
        this.users = [...this.users, ...chunk];
        index += chunkSize;

        if (index < data.length) {
          setTimeout(loadChunk, 0);
        } else {
          whenRendered(() => {
            this.renderTracker.notifyRendered();
            setTimeout(() => {
              console.log('APP_READY_UsersList');
            }, 3000);
          });
        }
      };

      loadChunk();
    });
  }

  showError(error: errorInterface) {
    this.hasErrors = error.errorState;
    this.submitErrors = [error.errorMessage];
  }

  handleDeleteClick(id: string) {
    this.service.delete(id).subscribe(() => {
      this.users = this.users.filter((user) => user.id !== id);
      this.toastService.showSuccess(MessageText.userDeleted);
    });
  }

  handleRestoreClick(id: string) {
    this.service.restore(id).subscribe(() => {
      this.getAllUsers();
    });
  }

  submitImport(importedUsers: ImportedUser[]) {
    this.waitingForImportResponse$ = true;
    this.service.confirmUsersImport(importedUsers).subscribe({
      next: () => {
        this.waitingForImportResponse$ = false;
        // Refresh the rendered list ([users]="users"), which is fed by getAllUsers().
        // reloadPage() only refreshes the Elf pagination store this page doesn't render.
        this.getAllUsers();
      },
      error: () => {
        this.waitingForImportResponse$ = false;
      },
    });
  }

  handleUserSelection(users: any) {
    this.selectedUsers = users;
    this.updateSelectionItems();
  }

  private updateSelectionItems() {
    this.items.forEach((item) => {
      if ((item as any).requiresSelection) {
        item.disabled = this.selectedUsers.length === 0;
      }
    });
  }

  deleteSelectedUsers() {
    const ids = this.selectedUsers.map((user) => user.id);
    this.service.deleteUsersByIds(ids).subscribe((complete) => {
      this.getAllUsers();
      this.confirmationService.close();
    });
  }

  asignUserToExistingGroup() {
    const dialogWidth = window.innerWidth > 768 ? '50%' : '90%';
    const dialogHeight = window.innerWidth > 768 ? '70%' : '80%';

    this.ref = this.dialogService.open(UserGroupsSelectionComponent, {
      header: 'Select groups',
    });

    this.ref?.onClose.subscribe((selectedData) => {
      if (selectedData) {
        const selectedUserIds = this.selectedUsers.map((user) => user.id);
        this.service
          .assіgnUserGroupsToUser(selectedUserIds, selectedData)
          .subscribe(() => {
            this.getAllUsers();
          });
      }
    });
  }

  unassіgnUserGroupsAndUser() {
    const dialogWidth = window.innerWidth > 768 ? '50%' : '90%';
    const dialogHeight = window.innerWidth > 768 ? '70%' : '80%';

    this.ref = this.dialogService.open(UserGroupsSelectionComponent, {
      header: 'Select groups',
    });

    this.ref?.onClose.subscribe((selectedData) => {
      if (selectedData) {
        const selectedUserIds = this.selectedUsers.map((user) => user.id);
        this.service
          .unassіgnUserGroupsAndUser(selectedUserIds, selectedData)
          .subscribe(() => {
            this.getAllUsers();
          });
      }
    });
  }

  confirmUsersDelete() {
    const message =
      this.translocoService.translate(`deleteConfirmationQuestion`) +
      ' ' +
      this.translocoService.translate(`selectedUsers`) +
      '?';

    const header = this.translocoService.translate('deleteConfirmation');

    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: 'pi pi-exclamation-triangle',
    });
  }

  fillActions() {
    const items: MenuItem[] = [];

    if (this.activeTenant && this.isTenantAdmin) {
      items.push(
        {
          label: this.translocoService.translate(`import`),
          icon: 'pi pi-upload',
          command: () => {
            this.importExport?.fileImport?.nativeElement.click();
          },
        },
        {
          label: this.translocoService.translate(`export`),
          icon: 'pi pi-download',
          command: () => {
            this.importExport?.export();
          },
        },
        { separator: true },
      );
    }

    items.push(
      {
        label: this.translocoService.translate(`assignToGroup`),
        icon: 'pi pi-plus',
        command: () => {
          this.asignUserToExistingGroup();
        },
        disabled: true,
        requiresSelection: true,
      } as MenuItem,
      {
        label: this.translocoService.translate(`unassignFromGroup`),
        icon: 'pi pi-times',
        command: () => {
          this.unassіgnUserGroupsAndUser();
        },
        disabled: true,
        requiresSelection: true,
      } as MenuItem,
      {
        label: this.translocoService.translate(`delete`),
        icon: 'pi pi-trash',
        command: () => {
          this.confirmUsersDelete();
        },
        disabled: true,
        requiresSelection: true,
      } as MenuItem,
    );

    this.items = items;
    this.updateSelectionItems();
  }
}
